import { ReactElement, useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { CabModal } from '@CabComponents/CabModal';
import { CabButton } from '@CabComponents/CabButton';
import CabCollapseMenu from '@CabComponents/CabCollapseMenu';
import { CabIcon } from '@CabComponents/CabIcon';
import CabRTEditor from '@CabComponents/CabRTEditor';
import { EventBrief } from '../../../store/cabinetApi/generated/crm';
import { useDispatch } from 'react-redux';
import { ThunkDispatchType } from '../../../store';
import { sendMessage } from '../../../store/globalMessages/actions';
import { Clipboard } from '@capacitor/clipboard';
import { PAGE_URL } from '../../../constants';
import { styled } from '@mui/material/styles';
import { IoCopyOutline, IoDocumentTextOutline, IoDownloadOutline, IoShareOutline } from 'react-icons/io5';
import colors from '../../../colors';



// eslint-disable-next-line max-len
const EMPTY_STATE = '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

export interface MeetingBriefModalProps {
  open: boolean;
  onClose: () => void;
  briefData: EventBrief | null | undefined;
  onUpdate?: (updatedBrief: Partial<EventBrief>) => Promise<void>;
  isUpdating?: boolean;
  meetingTitle: string;
  meetingTime?: string;
  meetingDate?: string;
  isLoading?: boolean;
}

const StyledGroupButton = styled(CabButton, {label: "StyledGroupButton"})({
  justifyContent: 'start', 
  color: colors.black900, 
  paddingBottom: 8, 
  paddingTop: 8, 
  paddingLeft: 15, 
  paddingRight: 15, 
  width: '100%'
});

export const MeetingBriefModal = ({
  open,
  briefData,
  onClose,
  onUpdate,
  isUpdating = false,
  meetingTitle,
  meetingTime,
  meetingDate,
  isLoading = false
}: MeetingBriefModalProps): ReactElement => {
  const [briefContent, setBriefContent] = useState(EMPTY_STATE);
  const [contentLoaded, setContentLoaded] = useState(false);
  const dispatch = useDispatch<ThunkDispatchType>();

  useEffect(() => {
    // If briefData is still undefined, keep loading
    if (briefData === undefined) {
      return;
    }
    
    // briefData is now defined
    if (briefData?.content && typeof briefData.content === 'object') {
      try {
        const jsonString = JSON.stringify(briefData.content);
        // Validate the JSON by parsing it back
        JSON.parse(jsonString);
        setBriefContent(jsonString);
        setContentLoaded(true);
      } catch (error) {
        console.error('Invalid JSON content:', error);
        setBriefContent(EMPTY_STATE);
        setContentLoaded(true);
      }
    } else {
      setBriefContent(EMPTY_STATE);
      setContentLoaded(true);
    }
  }, [briefData]);

  const handleSave = async () => {
    try {
      await onUpdate?.({
        content: JSON.parse(briefContent)
      });
      // onClose();
    } catch (error) {
      console.error('Failed to save brief:', error);
    }
  };

  const handleCancel = () => {
    setBriefContent(EMPTY_STATE);
    onClose();
  };

  const handleCopyLink = async () => {
    if (!briefData?.url_key || !meetingTitle) {
      return;
    }

    // Convert meeting title to snake case and take first 30 characters
    const slug = meetingTitle
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
      .replace(/\s+/g, '-') // Replace spaces with underscores
      .substring(0, 30)
      .replace(/-+$/, ''); // Remove trailing underscores

    const briefUrl = `${window.location.origin}${PAGE_URL.BRIEF}/${slug}/${briefData.url_key}`;
    
    try {
      await Clipboard.write({ string: briefUrl });
      dispatch(sendMessage({
        timeout: 750,
        message: 'Link copied to clipboard',
        header: "",
        position: {
          vertical: 'bottom',
          horizontal: 'center'
        },
        active: true,
        severity: 'success',
        autoDismiss: true
      }));
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleCopyAsText = async () => {
    if (!briefData?.content) {
      return;
    }

    try {
      // Extract plain text from the rich text content
      let plainText = '';
      if (typeof briefData.content === 'object') {
        // This is a simplified text extraction - in a real implementation,
        // you'd want to properly parse the rich text structure
        plainText = JSON.stringify(briefData.content);
      }
      
      await Clipboard.write({ string: plainText });
      dispatch(sendMessage({
        timeout: 750,
        message: 'Brief content copied to clipboard',
        header: "",
        position: {
          vertical: 'bottom',
          horizontal: 'center'
        },
        active: true,
        severity: 'success',
        autoDismiss: true
      }));
    } catch (error) {
      console.error('Failed to copy brief content:', error);
    }
  };

  const handleExportPDF = async () => {
    // Placeholder for PDF export functionality
    console.log('Export PDF functionality would be implemented here');
    dispatch(sendMessage({
      timeout: 750,
      message: 'PDF export coming soon',
      header: "",
      position: {
        vertical: 'bottom',
        horizontal: 'center'
      },
      active: true,
      severity: 'info',
      autoDismiss: true
    }));
  };
  
  return (
    <CabModal
      open={open}
      onClose={handleCancel}
      title={meetingTitle}
      closeIcon
      sx={{
        '& .MuiDialog-paper': {
          width: '80%',
          maxWidth: 1200,
          maxHeight: '80vh',
        }
      }}
      actionButtons={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            paddingBottom: 1,
            paddingLeft: 2,
            paddingRight: 2
          }}>
          {briefData?.url_key && meetingTitle && (
            <CabCollapseMenu
              target={
                <CabButton
                  buttonType="secondary"
                  endIcon={<CabIcon Icon={IoShareOutline} />}
                >
                  Share
                </CabButton>
              }
              buttonGroupSx={{paddingTop: 1, paddingBottom: 1, minWidth: "150px"}}
              buttonGroupColor="inherit"
              buttonGroupVariant="outlined"
              buttons={<>
                <StyledGroupButton 
                  buttonType="text"
                  onClick={handleCopyLink} 
                  icon={<CabIcon Icon={IoCopyOutline} color={colors.black900}/>}
                >
                  Copy Link
                </StyledGroupButton>
                <StyledGroupButton 
                  buttonType="text"
                  onClick={handleExportPDF} 
                  icon={<CabIcon Icon={IoDownloadOutline} color={colors.black900}/>}
                >
                  Export PDF
                </StyledGroupButton>
                <StyledGroupButton 
                  buttonType="text"
                  onClick={handleCopyAsText} 
                  icon={<CabIcon Icon={IoDocumentTextOutline} color={colors.black900}/>}
                >
                  Copy as Text
                </StyledGroupButton>
              </>}
              popOverTitle=""
              popOverAnchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom'
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              gap: 1
            }}>
            <CabButton
              buttonType="secondary"
              onClick={handleCancel}
            >
              Done
            </CabButton>
            <CabButton
              onClick={handleSave}
              disabled={isUpdating}
              sx={{ width: 140 }}
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </CabButton>
          </Box>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '60vh' }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%"
            }}>
            <CircularProgress />
          </Box>
        ) : (
          <CabRTEditor
            key={`${briefData?.id || 'empty'}-${contentLoaded ? 'loaded' : 'loading'}`}
            placeholder="Start writing your meeting brief..."
            onChange={setBriefContent}
            editorState={briefContent}
            autoFocus={false}
            sx={{
              flex: 1, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              '& > div:last-child': {
                flex: 1,
                overflow: 'auto',
                minHeight: 0
              },
              '& .editor-input': {
                height: '100% !important',
                overflow: 'auto !important',
                minHeight: '150px !important',
              }
            }}
          />
        )}
      </Box>
    </CabModal>
  );
};

export default MeetingBriefModal;
